export type NavIconName = "home" | "users" | "baby" | "user-round" | "shield" | "credit-card" | "smartphone" | "book-open"

export interface NavItem {
  /** i18n key — the label is resolved at render time so it follows the language. */
  labelKey: string
  to: string
  icon: NavIconName
  // `exact` = only active on an exact path match (used for the root route).
  exact?: boolean
  /** Same code as the route's `meta.permission`; the item is hidden without it. */
  permission?: string
}

/**
 * A detail page (`/children/:id`) is a SIBLING route record of its list, not a
 * child of it, so RouterLink's own `isActive` does not light the nav item up.
 * Section membership is a path-prefix question — answer it here.
 */
export function isNavItemActive(item: NavItem, path: string): boolean {
  if (item.exact)
    return path === item.to

  return path === item.to || path.startsWith(`${item.to}/`)
}

export interface NavGroup {
  /** i18n key for the group heading; also the group's stable key. */
  labelKey: string
  items: NavItem[]
}

export const navGroups: NavGroup[] = [
  {
    labelKey: "nav.menu",
    items: [
      { labelKey: "nav.home", to: "/", icon: "home", exact: true, permission: "dashboard.view" },
      // The catalogue has no parents/children codes — both lists are user records,
      // and the API gates them with the same `users.view` as the admin list.
      { labelKey: "nav.parents", to: "/parents", icon: "user-round", permission: "users.view" },
      { labelKey: "nav.children", to: "/children", icon: "baby", permission: "users.view" },
      { labelKey: "nav.subscriptions", to: "/subscriptions", icon: "credit-card", permission: "subscriptions.view" },
    ],
  },
  {
    labelKey: "nav.system",
    items: [
      { labelKey: "nav.users", to: "/users", icon: "users", permission: "users.view" },
      { labelKey: "nav.appVersions", to: "/app-versions", icon: "smartphone", permission: "appversions.view" },
      { labelKey: "nav.deviceGuides", to: "/device-guides", icon: "book-open", permission: "deviceguides.view" },
      { labelKey: "nav.roles", to: "/roles", icon: "shield", permission: "roles.view" },
    ],
  },
]

/** The groups the signed-in admin may open; a group left empty is dropped. */
export function visibleNavGroups(groups: NavGroup[], can: (code: string) => boolean): NavGroup[] {
  return groups
    .map(group => ({ ...group, items: group.items.filter(item => !item.permission || can(item.permission)) }))
    .filter(group => group.items.length > 0)
}

/**
 * Landing target for an admin who may not open the route they asked for. Every
 * section is permission-gated now, so "send them home" can bounce forever —
 * the first section they do own is the only safe answer.
 */
export function firstAllowedPath(groups: NavGroup[], can: (code: string) => boolean): string | null {
  return visibleNavGroups(groups, can)[0]?.items[0]?.to ?? null
}
